import { useState } from "react";
import { createPortal } from "react-dom";
import { OutlineButton } from "@components/Buttons";
import useEscapeKey from "@hooks/useEscapeKey";
import { useTheme } from "@hooks/useTheme";
import { COLOR_CONFLICT_FLAG } from "@constants/index";

export const ComponentConflictStatusGuideModal = ({
  licensePolicyData = [],
  title,
  colors,
  t,
  showIcon = true,
  showObligationLevel = true,
  showVulnerableLevel = true,
  "data-testid": dataTestId = "component-conflict-status-guide-modal",
}) => {
  const { colors: themeColors } = useTheme();
  const resolvedColors = colors ?? themeColors;
  const fieldContainerTestId = (name) => `${dataTestId}-field-${name}-container`;
  const fieldLabelTestId = (name) => `${dataTestId}-field-${name}-label`;
  const fieldValueTestId = (name) => `${dataTestId}-field-${name}-value`;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);
  useEscapeKey(closeModal, isModalOpen);

  const colorMap = {
    black: "bg-black",
    red: "bg-[#CC1010]",
    orange: "bg-[#FF8300]",
    steelblue: "bg-[#355dff]",
    green: "bg-[#1C991A]",
  };

  const modalContent = (
    <div className={`fixed inset-0 ${colors?.bgBlurModal ?? ""} z-50 flex items-center justify-start md:justify-center p-4 backdrop-blur-[1px] overflow-x-auto`} data-testid={`${dataTestId}-overlay`}>
      <div className={`${colors?.bgModal ?? resolvedColors.bgModal} w-[560px] min-w-[560px] max-h-[90vh] flex-shrink-0 overflow-auto rounded-lg shadow-xl xl:overflow-y-hidden flex flex-col`} data-testid={dataTestId}>
        {/* Header */}
        <div className={`px-6 py-4 border-b ${resolvedColors.borderPrimary} flex-shrink-0`} data-testid={`${dataTestId}-header`}>
          <h2 className={`text-xl font-bold text-center ${resolvedColors.textStaticWhite} uppercase tracking-wide`} data-testid={`${dataTestId}-title`}>
            {t("component.conflict-status-guide-title", "COMPONENT CONFLICT STATUS GUIDE")}
          </h2>
        </div>

        {/* Content */}
        <div className="px-6 py-4 pb-4 flex-1 min-h-0 overflow-y-auto" data-testid={`${dataTestId}-content`}>
          <div className={`${resolvedColors.bgInput} border ${resolvedColors.borderPrimary} rounded-lg p-4 space-y-4 text-left`} data-testid={fieldContainerTestId("guide-body")}>
            {showObligationLevel && (
              <div data-testid={fieldContainerTestId("obligation-type")}>
                <h4 className={`font-medium ${resolvedColors.textStaticWhite} mb-2 text-base tracking-normal`} data-testid={fieldLabelTestId("obligation-type")}>{t("component.obligation-type", "Obligation Type")}</h4>
                <div className="space-y-2 rounded-lg p-4" data-testid={fieldContainerTestId("obligation-type-list")}>
                  {licensePolicyData?.licensePolicySteps
                    ?.map((step) => {
                  const colorClass = colorMap[step.policyStepColor] || resolvedColors.bgInput;
                      const stepKey = `obligation-step-${step.licensePolicyStepSeq}`;
                      return (
                        <div key={step.licensePolicyStepSeq} className="flex items-center space-x-2 w-full" data-testid={fieldContainerTestId(stepKey)}>
                          <div className={`w-14 h-6 ${colorClass} rounded flex-shrink-0`} data-testid={fieldValueTestId(`${stepKey}-color`)} />
                          <span title={step.remark || "---"} className={`font-normal tracking-normal text-sm ${resolvedColors.textStaticWhite} line-clamp-2 h-10 content-center`} data-testid={fieldValueTestId(stepKey)}>{step.remark || "---"}</span>
                        </div>
                      );
                    })
                  }
                  <div className="flex items-center space-x-2" data-testid={fieldContainerTestId("undefined-obligation-type")}>
                    <div
                      className="w-14 h-6 rounded flex-shrink-0"
                      style={{ backgroundColor: COLOR_CONFLICT_FLAG.UNDEFINED_CONFLICT }}
                      data-testid={fieldValueTestId("undefined-obligation-type-color")}
                    />
                    <span className={`font-normal text-sm tracking-normal ${resolvedColors.textStaticWhite} line-clamp-2 h-10 content-center`} data-testid={fieldValueTestId("undefined-obligation-type")}>
                      {t("component.undefined-obligation-type")}
                    </span>
                  </div>
                  <div className="flex items-center space-x-2" data-testid={fieldContainerTestId("undefined-license")}>
                    <div className={`w-14 h-6 ${resolvedColors.bgConflictUndefined} border ${resolvedColors.borderDisabled} rounded flex-shrink-0`} data-testid={fieldValueTestId("undefined-license-color")} />
                    <span className={`font-normal text-sm tracking-normal ${resolvedColors.textStaticWhite} line-clamp-2 h-10 content-center`} data-testid={fieldValueTestId("undefined-license")}>
                      {t("component.undefined-license")}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {showVulnerableLevel && (
              <div data-testid={fieldContainerTestId("vulnerability-security-code")}>
                <h4 className={`font-medium ${resolvedColors.textStaticWhite} mb-2 text-base tracking-normal`} data-testid={fieldLabelTestId("vulnerability-security-code")}>{t("vulnerability-policy.vulnerability-security-code")}</h4>
                <div className="space-y-3 rounded-lg p-4" data-testid={fieldContainerTestId("vulnerability-security-code-list")}>
                  <div className="flex items-center" data-testid={fieldContainerTestId("vuln-level-s")}>
                    <div className="flex space-x-1 w-60 flex-shrink-0" data-testid={fieldLabelTestId("vuln-level-s")}>
                      <div className="w-6 h-6 rounded flex items-center justify-center">
                        <span className={`${resolvedColors.textStaticWhite} text-xs font-bold`} data-testid={fieldValueTestId("vuln-level-s-code")}>S</span>
                      </div>
                    </div>
                    <span className={`font-normal text-sm tracking-normal ${resolvedColors.textStaticWhite}`} data-testid={fieldValueTestId("vuln-level-s")}>{t("component.remove-or-use-other", "Remove or Use Other Components")}</span>
                  </div>
                  <div className="flex items-center" data-testid={fieldContainerTestId("vuln-level-abc")}>
                    <div className="flex space-x-1 w-60 flex-shrink-0" data-testid={fieldLabelTestId("vuln-level-abc")}>
                      <div className="w-6 h-6 rounded flex items-center justify-center">
                        <span className={`${resolvedColors.textStaticWhite} text-xs font-bold`} data-testid={fieldValueTestId("vuln-level-a-code")}>A</span>
                      </div>
                      <div className="w-6 h-6 rounded flex items-center justify-center">
                        <span className={`${resolvedColors.textStaticWhite} text-xs font-bold`} data-testid={fieldValueTestId("vuln-level-b-code")}>B</span>
                      </div>
                      <div className="w-6 h-6 rounded flex items-center justify-center">
                        <span className={`${resolvedColors.textStaticWhite} text-xs font-bold`} data-testid={fieldValueTestId("vuln-level-c-code")}>C</span>
                      </div>
                    </div>
                    <span className={`font-normal text-sm tracking-normal ${resolvedColors.textStaticWhite}`} data-testid={fieldValueTestId("vuln-level-abc")}>{t("component.emergency-patch-required", "Emergency Patch Required")}</span>
                  </div>
                  <div className="flex items-center" data-testid={fieldContainerTestId("vuln-level-defgh")}>
                    <div className="flex space-x-1 w-60 flex-shrink-0" data-testid={fieldLabelTestId("vuln-level-defgh")}>
                      <div className="w-6 h-6 rounded flex items-center justify-center">
                        <span className={`${resolvedColors.textStaticWhite} text-xs font-bold`} data-testid={fieldValueTestId("vuln-level-d-code")}>D</span>
                      </div>
                      <div className="w-6 h-6 rounded flex items-center justify-center">
                        <span className={`${resolvedColors.textStaticWhite} text-xs font-bold`} data-testid={fieldValueTestId("vuln-level-e-code")}>E</span>
                      </div>
                      <div className="w-6 h-6 rounded flex items-center justify-center">
                        <span className={`${resolvedColors.textStaticWhite} text-xs font-bold`} data-testid={fieldValueTestId("vuln-level-f-code")}>F</span>
                      </div>
                      <div className="w-6 h-6 rounded flex items-center justify-center">
                        <span className={`${resolvedColors.textStaticWhite} text-xs font-bold`} data-testid={fieldValueTestId("vuln-level-g-code")}>G</span>
                      </div>
                      <div className="w-6 h-6 rounded flex items-center justify-center">
                        <span className={`${resolvedColors.textStaticWhite} text-xs font-bold`} data-testid={fieldValueTestId("vuln-level-h-code")}>H</span>
                      </div>
                    </div>
                    <span className={`font-normal text-sm tracking-normal ${resolvedColors.textStaticWhite}`} data-testid={fieldValueTestId("vuln-level-defgh")}>{t("component.emergency-patch-required", "Emergency Patch Required")}</span>
                  </div>
                  <div className="flex items-center" data-testid={fieldContainerTestId("vuln-level-ijklmno")}>
                    <div className="flex space-x-1 w-60 flex-shrink-0" data-testid={fieldLabelTestId("vuln-level-ijklmno")}>
                      <div className="w-6 h-6 rounded flex items-center justify-center">
                        <span className="text-white text-xs font-bold" data-testid={fieldValueTestId("vuln-level-i-code")}>I</span>
                      </div>
                      <div className="w-6 h-6 rounded flex items-center justify-center">
                        <span className="text-white text-xs font-bold" data-testid={fieldValueTestId("vuln-level-j-code")}>J</span>
                      </div>
                      <div className="w-6 h-6 rounded flex items-center justify-center">
                        <span className="text-white text-xs font-bold" data-testid={fieldValueTestId("vuln-level-k-code")}>K</span>
                      </div>
                      <div className="w-6 h-6 rounded flex items-center justify-center">
                        <span className="text-white text-xs font-bold" data-testid={fieldValueTestId("vuln-level-l-code")}>L</span>
                      </div>
                      <div className="w-6 h-6 rounded flex items-center justify-center">
                        <span className="text-white text-xs font-bold" data-testid={fieldValueTestId("vuln-level-m-code")}>M</span>
                      </div>
                      <div className="w-6 h-6 rounded flex items-center justify-center">
                        <span className="text-white text-xs font-bold" data-testid={fieldValueTestId("vuln-level-n-code")}>N</span>
                      </div>
                      <div className="w-6 h-6 rounded flex items-center justify-center">
                        <span className="text-white text-xs font-bold" data-testid={fieldValueTestId("vuln-level-o-code")}>O</span>
                      </div>
                    </div>
                    <span className="font-normal text-sm tracking-normal text-white" data-testid={fieldValueTestId("vuln-level-ijklmno")}>{t("component.periodic-monitoring-required")}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className={`px-6 py-4 border-t ${resolvedColors.borderPrimary} flex justify-center flex-shrink-0`} data-testid={`${dataTestId}-footer`}>
          <OutlineButton data-testid={`${dataTestId}-close-button`} onClick={closeModal}>{t("common.close-button", "Close")}</OutlineButton>
        </div>
      </div>
    </div>
  );

  return (
    <div style={{ display: "ruby" }} data-testid={fieldContainerTestId("trigger-wrapper")}>
      <span data-testid={fieldValueTestId("title")}>{title}</span>
      {showIcon && (
        <>
          <svg
            className={`ml-2 mb-0.5 h-5 w-5 ${resolvedColors.textComparisonLink} cursor-pointer ${resolvedColors.hoverTextComparisonLink}`}
            fill="currentColor"
            viewBox="0 0 20 20"
            onClick={openModal}
            width={20}
            height={20}
            data-testid={`${dataTestId}-trigger`}
          >
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
              clipRule="evenodd"
            />
          </svg>

          {isModalOpen && createPortal(modalContent, document.body)}
        </>
      )}
    </div>
  );
};

export default ComponentConflictStatusGuideModal;
